import React from 'react';
import {Layout, Typography, Table} from 'antd';
import Header from "../../../components/molecules/Header/component";
import Footer from "../../../components/molecules/Footer/component";

const {Content} = Layout;
const {Title, Paragraph, Text} = Typography;

const retentionColumns = [
    {title: 'Data category', dataIndex: 'category', key: 'category'},
    {title: 'Retention period', dataIndex: 'period', key: 'period'},
    {title: 'Purpose', dataIndex: 'purpose', key: 'purpose'},
    {title: 'Notes', dataIndex: 'notes', key: 'notes'}
];

const retentionData = [
    {
        key: 'account',
        category: 'Account data',
        period: 'While account is active + 12 months after closure',
        purpose: 'Maintain records and support possible account recovery',
        notes: 'Removed or anonymised after the retention period'
    },
    {
        key: 'payment',
        category: 'Payment records',
        period: '6 years',
        purpose: 'Fulfil accounting, auditing, and legal obligations',
        notes: 'Kept to comply with tax and audit requirements'
    },
    {
        key: 'analytics',
        category: 'Analytics / click data',
        period: 'Active use; then anonymised after 12 months',
        purpose: 'Analyse campaign performance and detect fraud',
        notes: 'Personal identifiers removed after 12 months'
    },
    {
        key: 'marketing',
        category: 'Marketing data',
        period: 'Until you withdraw consent',
        purpose: 'Manage communication preferences and promotions',
        notes: 'Deleted when you revoke consent'
    }
];

const PrivacyPolicyPage: React.FC = () => {
    return (
        <div>
            <Header/>
            <Content style={{maxWidth: 900, margin: '0 auto', padding: '32px 16px 48px'}}>
                <Typography>
                    <Title level={2}>Promotify AI – Privacy Policy</Title>

                    <Paragraph>
                        <Text strong>Effective date:</Text> 15/10/2025
                        <br/>
                        <Text strong>Company:</Text> Promotify AI Ltd
                        <br/>
                        <Text strong>Address:</Text> 210 Leigh Road, Leigh-on-Sea, England, SS9 1BS
                        <br/>
                        <Text strong>Contact:</Text>{' '}
                        <a href="mailto:info@promotifyai.com">info@promotifyai.com</a>
                    </Paragraph>

                    <Title level={3}>1. Introduction</Title>
                    <Paragraph>
                        Promotify AI Ltd (<Text italic>“Promotify”</Text>) operates the Promotify AI marketing
                        platform (<Text italic>“Platform”</Text>). We are committed to protecting your privacy and
                        ensuring that your personal information is handled in a safe and responsible manner. This
                        Privacy Policy describes how we collect, use, store, and protect your personal data when you
                        use our Platform. We handle your information in line with the UK General Data Protection
                        Regulation (UK GDPR), the EU GDPR, and the Data Protection Act 2018.
                    </Paragraph>

                    <Title level={3}>2. Data Controller</Title>
                    <Paragraph>
                        Promotify AI Ltd is the organisation responsible for deciding how and why your personal data
                        is processed when you use our website and Platform.
                        For any questions, requests, or concerns regarding your data, you can contact our Data
                        Protection Officer (DPO) at:{' '}<a href="mailto:info@promotifyai.com">info@promotifyai.com</a>,
                        or write to us at 210 Leigh Road, Leigh-on-Sea, England, SS9 1BS.
                    </Paragraph>

                    <Title level={3}>3. Data we collect</Title>
                    <Paragraph>We only collect the information needed to provide and secure our services. This
                        includes:</Paragraph>
                    <Paragraph>
                        <ul>
                            <li>
                                <Text strong>Account information:</Text> such as your name, email address, and
                                password.
                            </li>
                            <li>
                                <Text strong>Payment data:</Text> details relating to your chosen payment method and
                                transaction history.
                            </li>
                            <li>
                                <Text strong>Campaign data:</Text> information about your campaigns, like URLs,
                                budgets, and click performance.
                            </li>
                            <li>
                                <Text strong>Analytics and technical data:</Text> IP address, device signals, cookies,
                                and usage logs used to keep the Platform secure and functioning correctly.
                            </li>
                            <li>
                                <Text strong>Marketing preferences:</Text> your opt-in choices and referral or
                                promotional tracking information.
                            </li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        We do <Text strong>not</Text> intentionally collect special categories of personal data (for
                        example, information about your race, religion, sexual orientation, or biometric identifiers).
                    </Paragraph>

                    <Title level={3}>4. How we use your information</Title>
                    <Paragraph>We use your personal data for the following purposes:</Paragraph>
                    <Paragraph>
                        <ul>
                            <li>To create, manage, and authenticate your user account.</li>
                            <li>To process payments and verify transactions for billing and fraud prevention.</li>
                            <li>To track campaign performance, including click activity and validity checks.</li>
                            <li>To communicate with you, for example about updates, support queries, or service
                                notices.
                            </li>
                            <li>To monitor and improve Platform performance, and to detect misuse or fraudulent
                                activity.
                            </li>
                            <li>To comply with legal, regulatory, and tax obligations.</li>
                        </ul>
                    </Paragraph>

                    <Title level={3}>5. Cookies and Tracking</Title>
                    <Paragraph>
                        Promotify uses technologies necessary to run the site, support authentication, keep accounts
                        secure, and prevent
                        abuse. This includes tools such as Cloudflare Turnstile and device or browser signals that help
                        validate clicks and
                        protect against fraud.
                    </Paragraph>
                    <Paragraph>
                        If you visit our signup page via a partner invite link, we store a cookie that holds the
                        invite identifier so we can link your registration to that partner. This cookie is removed
                        once you complete signup.
                    </Paragraph>

                    <Paragraph>
                        We also use <Text strong>Google Analytics</Text> to understand how visitors use our website and
                        to improve the
                        Platform. Google Analytics may set cookies or use similar technologies to collect usage and
                        technical information
                        (such as pages visited, approximate location, device/browser information, and interactions).
                    </Paragraph>

                    <Paragraph>
                        We also use <Text strong>PostHog</Text> analytics to understand how users move through key
                        Platform flows, such as signup, trial activation, campaign activity, referrals, subscriptions,
                        and feature usage. PostHog helps us improve the product, measure conversion and retention, and
                        detect issues in user journeys. The events we send may include account role, subscription
                        status, campaign or referral identifiers, transaction amounts where relevant, page paths,
                        browser/device information, and similar usage data.
                    </Paragraph>

                    <Paragraph>
                        Where required by law, analytics will be enabled only after you provide consent via a cookie
                        notice/consent banner.
                        You can change your preferences at any time through your browser settings or any cookie settings
                        link provided on
                        the site (where applicable).
                    </Paragraph>

                    <Title level={3}>6. Sharing your data</Title>
                    <Paragraph>
                        We may share limited personal data with carefully selected third-party service providers who
                        act as data processors on our behalf. These include, for example:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>
                                <Text strong>Payment processors:</Text> such as Stripe, to handle payments securely.
                            </li>
                            <li>
                                <Text strong>Hosting and infrastructure providers:</Text> including AWS, Heroku, and
                                MongoDB Atlas.
                            </li>
                            <li>
                                <Text strong>Domain and DNS services:</Text> such as GoDaddy for domain and DNS
                                management.
                            </li>
                            <li>
                                <Text strong>Analytics providers:</Text> such as Google Analytics and PostHog, to help
                                us understand website and Platform usage, product performance, and user journeys.
                            </li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        All such providers are bound by written Data Processing Agreements (DPAs) and must follow
                        GDPR-level
                        data protection standards. They may only process your data in line with our documented
                        instructions.
                    </Paragraph>

                    <Title level={3}>7. International Data Transfers</Title>
                    <Paragraph>
                        Some of our service providers may process data outside the UK or European Economic Area. When
                        this happens, we ensure that appropriate safeguards are in place, such as Standard Contractual
                        Clauses (SCCs) or the UK International Data Transfer Agreement (IDTA), so that your data is
                        protected to a level essentially equivalent to UK and EU law.
                    </Paragraph>

                    <Title level={3}>8. Data Retention</Title>
                    <Paragraph>
                        We keep personal data only for as long as it is needed for the purposes described in this
                        policy, or as required by law. After the relevant retention period, data is deleted or
                        anonymised.
                    </Paragraph>
                    <Table
                        columns={retentionColumns}
                        dataSource={retentionData}
                        pagination={false}
                        size="small"
                        style={{margin: '16px 0'}}
                    />

                    <Title level={3}>9. Data Security</Title>
                    <Paragraph>
                        We use a range of organisational and technical measures to protect your personal data,
                        including:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>Transport-level encryption (HTTPS/TLS) for data in transit.</li>
                            <li>Secure password storage using hashing algorithms (e.g. bcrypt/crypto-js).</li>
                            <li>Role-based access controls to limit access to authorised user typology.</li>
                            <li>Two-factor authentication at sign-up to enhance account protection.</li>
                            <li>Regular backups and internal reviews of security practices.</li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        If we become aware of a personal data breach that may pose a risk to your rights and
                        freedoms, we will follow a structured incident-response process. This includes identifying and
                        containing the breach, assessing its impact, notifying key internal teams, preserving
                        evidence, and, where required, informing the UK Information Commissioner’s Office (ICO) and
                        affected users within 72 hours. We will also address any vulnerabilities and carry out a
                        post-incident review.
                    </Paragraph>

                    <Title level={3}>10. Your rights</Title>
                    <Paragraph>
                        Under applicable data protection laws (including the UK GDPR and EU GDPR), you have several
                        rights in relation to your personal data. These include the right to:
                    </Paragraph>
                    <Paragraph>
                        <ul>
                            <li>Request access to the personal data we hold about you.</li>
                            <li>Ask us to correct inaccurate or incomplete information.</li>
                            <li>Request deletion of your data in certain circumstances (“right to be forgotten”).</li>
                            <li>Request that we restrict or object to certain types of processing.</li>
                            <li>Receive your data in a structured, commonly used format and have it transferred to
                                another provider (data portability).
                            </li>
                        </ul>
                    </Paragraph>
                    <Paragraph>
                        To exercise any of these rights, contact us at{' '}
                        <a href="mailto:info@promotifyai.com">info@promotifyai.com</a>. We aim to respond within 30
                        days, in line with legal requirements.
                    </Paragraph>

                    <Title level={3}>11. Children’s Privacy</Title>
                    <Paragraph>
                        The Promotify Platform is designed for users who are 18 years of age or older. We do not
                        knowingly collect personal data from individuals under 18. If we learn that such data has been
                        collected inadvertently, we will delete it as soon as reasonably possible.
                    </Paragraph>

                    <Title level={3}>12. Changes to this Privacy Policy</Title>
                    <Paragraph>
                        We may update this Privacy Policy from time to time. The most recent version will always be
                        available on our website and will include an updated effective date. Where changes are
                        significant, we will take additional steps to inform you, such as sending a notification by
                        email.
                    </Paragraph>

                    <Title level={3}>13. Contact Information</Title>
                    <Paragraph>
                        If you have any questions, concerns, or complaints about this Privacy Policy or how your data
                        is handled, please contact us at{' '}
                        <a href="mailto:info@promotifyai.com">info@promotifyai.com</a>.
                        You also have the right to raise a concern with the UK Information Commissioner’s Office
                        (ICO). For more information, visit{' '}
                        <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">
                            https://ico.org.uk
                        </a>.
                    </Paragraph>
                </Typography>
            </Content>
            <Footer/>
        </div>
    );
};

export default PrivacyPolicyPage;
